import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getMySalon, retryCampaignSetup } from "@/lib/salon.functions";
import { bookBoostDays, cancelBoostDay } from "@/lib/boost.functions";
import { confirmCheckout } from "@/lib/payments.functions";
import { BoostCalendar } from "@/components/BoostCalendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  berlinToday,
  earliestBoostDate,

  formatDateLong,
  formatDateShort,
  isDateBoostable,
  nextBoostableDate,
} from "@/lib/salonturbo";
import { Loader2, Sparkles } from "lucide-react";


export const Route = createFileRoute("/_authenticated/dashboard/")({
  component: DashboardOverview,
});

function DashboardOverview() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fetchSalon = useServerFn(getMySalon);
  const book = useServerFn(bookBoostDays);
  const cancel = useServerFn(cancelBoostDay);
  const confirm = useServerFn(confirmCheckout);
  const retryCampaign = useServerFn(retryCampaignSetup);

  const { data, isLoading } = useQuery({ queryKey: ["my-salon"], queryFn: () => fetchSalon() });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("checkout") !== "success" || !sessionId) return;
    void confirm({ data: { sessionId } })
      .then(() => toast.success("Coins wurden gutgeschrieben."))
      .catch(() => toast.info("Zahlung eingegangen – Guthaben wird verarbeitet."))
      .finally(() => {
        void queryClient.invalidateQueries({ queryKey: ["my-salon"] });
        window.history.replaceState({}, "", "/dashboard");
      });
  }, [confirm, queryClient]);

  const bookMutation = useMutation({
    mutationFn: (dates: string[]) => book({ data: { salonId: data!.salon!.id, dates } }),
    onSuccess: async () => {
      toast.success("Boost-Tage gebucht.");
      await queryClient.invalidateQueries({ queryKey: ["my-salon"] });
    },
    onError: (error) =>
      toast.error(
        String(error).includes("insufficient") ? "Nicht genug Coins." : "Buchung fehlgeschlagen.",
      ),
  });

  const cancelMutation = useMutation({
    mutationFn: (boostDayId: string) => cancel({ data: { boostDayId } }),
    onSuccess: async () => {
      toast.success("Boost storniert, Coin zurück auf dem Konto.");
      await queryClient.invalidateQueries({ queryKey: ["my-salon"] });
    },
    onError: () => toast.error("Stornierung nicht möglich."),
  });

  const campaignMutation = useMutation({
    mutationFn: () => retryCampaign(),
    onSuccess: async (result) => {
      if (result.campaignId) toast.success("Google-Kampagne wurde angelegt (pausiert).");
      else toast.error("Kampagne konnte nicht angelegt werden. Support ist informiert.");
      await queryClient.invalidateQueries({ queryKey: ["my-salon"] });
    },
    onError: () => toast.error("Kampagne konnte nicht angelegt werden."),
  });


  const today = berlinToday();
  const boostedDates = useMemo(() => {
    const map: Record<string, string> = {};
    for (const day of data?.boostDays ?? []) {
      if (day.status !== "cancelled") map[day.boost_date] = day.status;
    }
    return map;
  }, [data]);

  // Am Tag des Onboarding-Abschlusses prüft Google die Kampagne noch – dann ab morgen.
  const earliest = useMemo(
    () => earliestBoostDate(data?.salon?.onboarding_completed_at ?? null),
    [data?.salon?.onboarding_completed_at],
  );
  const inGoogleReview = earliest > today;

  // Heute ist nur buchbar, solange die Ads-Endzeit (2h vor Schließung) nicht erreicht ist.
  const todayBookable = useMemo(
    () => isDateBoostable(today, data?.hours ?? [], new Date(), earliest),
    [today, earliest, data?.hours],
  );
  const nextDate = useMemo(
    () => nextBoostableDate(data?.hours ?? [], boostedDates, new Date(), earliest),
    [data?.hours, boostedDates, earliest],
  );
  const focusDate = todayBookable ? today : nextDate;


  const upcoming = (data?.boostDays ?? [])
    .filter((day) => day.boost_date >= today && day.status !== "cancelled")
    .sort((a, b) => a.boost_date.localeCompare(b.boost_date));
  const past = (data?.boostDays ?? []).filter(
    (day) => day.boost_date < today && day.status !== "cancelled",
  );




  if (isLoading || !data?.salon) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fokus-Element: ein großer, ruhiger Button für den heutigen Boost. */}
      <Card className="overflow-hidden border-none bg-card shadow-soft-lg">
        <CardContent className="flex flex-col items-center gap-5 px-6 py-10 text-center sm:py-14">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {formatDateLong(focusDate ?? today)}
          </p>
          {todayBookable && boostedDates[today] ? (
            <>
              <h2 className="font-display text-2xl font-medium sm:text-3xl">
                Boost für heute läuft
              </h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                Deine Anzeigen sind aktiv. Lehn dich zurück – wir bringen kurzfristige Termine rein.
              </p>
              <span className="inline-flex items-center gap-2 rounded-2xl bg-primary/10 px-5 py-3 text-sm font-medium text-primary">
                <Sparkles className="size-4" /> Aktiv
              </span>
            </>
          ) : !focusDate ? (
            <>
              <h2 className="font-display text-2xl font-medium sm:text-3xl">
                Alles gebucht – nichts zu tun
              </h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                In den nächsten 30 Tagen ist kein weiterer Tag frei oder buchbar.
              </p>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-medium sm:text-3xl">
                {inGoogleReview
                  ? "Deine Kampagne wird noch geprüft"
                  : todayBookable
                    ? "Heute noch Lücken im Kalender?"
                    : "Für heute ist der Boost zu spät"}
              </h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                {inGoogleReview
                  ? "Google prüft deine neue Kampagne noch (bis zu 24 Stunden). Ab morgen kannst du deine Tage boosten."
                  : todayBookable
                    ? "Ein Tippen genügt – wir machen dich sofort bei Kunden sichtbar, die jetzt einen Termin suchen."
                    : "Die Anzeigen laufen 2 Stunden vor Schließung aus. Wir starten deinen Boost am nächsten möglichen Tag."}
              </p>

              <Button
                size="lg"
                className="h-16 w-full max-w-sm rounded-3xl text-base"
                disabled={bookMutation.isPending}
                onClick={() => {
                  if (data.coins < 1) {
                    toast.info("Du brauchst mindestens 1 Coin – lade jetzt auf.");
                    void navigate({ to: "/dashboard/coins" });
                    return;
                  }
                  bookMutation.mutate([focusDate]);
                }}
              >
                {bookMutation.isPending ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <Sparkles className="size-5" />
                )}
                {todayBookable
                  ? "Boost für heute starten"
                  : `Boost für ${formatDateShort(focusDate)} starten`}
              </Button>
              <p className="text-xs text-muted-foreground">
                Kostet 1 Coin · du hast {data.coins}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {data.coins === 0 && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
            <p className="text-sm">
              Du hast keine Coins mehr. Lade auf, um weitere Tage zu boosten.
            </p>
            <Button asChild size="sm">
              <Link to="/dashboard/coins">Coins aufladen</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {(!data.salon.google_campaign_id || data.salon.google_campaign_status === "error") && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
            <p className="text-sm">
              Für deinen Salon ist noch keine Google-Kampagne hinterlegt. Ohne Kampagne können
              gebuchte Tage nicht live gehen.
            </p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => campaignMutation.mutate()}
              disabled={campaignMutation.isPending}
            >
              {campaignMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              Kampagne erneut anlegen
            </Button>
          </CardContent>
        </Card>
      )}


      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl">Tage boosten</CardTitle>
          <CardDescription>
            Wähle die Tage aus, an denen du Auslastung brauchst – bis 30 Tage im Voraus.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <BoostCalendar
            hours={data.hours}
            boostedDates={boostedDates}
            coins={data.coins}
            pending={bookMutation.isPending}
            earliestDate={earliest}

            onBook={(dates) => bookMutation.mutate(dates)}
            onNeedCoins={(needed) => {
              toast.info(`Du brauchst ${needed} Coin(s) mehr – lade jetzt auf.`);
              void navigate({ to: "/dashboard/coins" });
            }}
          />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl">Geplante Boosts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcoming.length === 0 && (
              <p className="text-sm text-muted-foreground">Noch keine Tage geplant.</p>
            )}
            {upcoming.map((day) => (
              <div key={day.id} className="flex items-center justify-between gap-3 border-b pb-3 last:border-0">
                <div>
                  <p className="text-sm font-medium">{formatDateLong(day.boost_date)}</p>
                  <p className="text-xs text-muted-foreground">
                    {day.status === "active" ? "Kampagne läuft" : "geplant"} · {day.daily_budget_eur} € Budget
                  </p>
                  {day.error_message && (
                    <p className="text-xs text-destructive">{day.error_message}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={day.status === "active" ? "default" : "secondary"}>
                    {day.status === "active" ? "aktiv" : "geplant"}
                  </Badge>
                  {day.status === "scheduled" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelMutation.mutate(day.id)}
                      disabled={cancelMutation.isPending}
                    >
                      Storno
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl">Vergangene Boost-Tage</CardTitle>
            <CardDescription>Deine bereits geboosteten Tage.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {past.length === 0 && (
              <p className="text-sm text-muted-foreground">Noch keine Boost-Tage vergangen.</p>
            )}
            {past.slice(0, 12).map((day) => (
              <div key={day.id} className="border-b pb-3 last:border-0">
                <p className="text-sm font-medium">{formatDateLong(day.boost_date)}</p>
              </div>
            ))}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
